import React, { Component } from "react";
import {
  company,
  loading,
  NavbarAdmin,
  Pagination,
  Modal,
  Select,
  DatePicker,
  alert,
  GET,
  POST,
  PUT,
  DELETE,
  t,
  ActionIconButton,
  ACTION_ICONS,
  searchCustomerRows,
} from "../../components/CustomComponent.js";
import { can } from "../../utils/permission.js";

import { formatNumber } from "../../utils/formatting";
// 1 โครงการ (project_code) มีได้หลาย WBS -> แยกเป็น 2 แท็บ: โครงการ / WBS (ตาราง wbs อ้างกลับมาที่โครงการ)
const TAB_PROJECT = "project";
const TAB_WBS = "wbs";
const PAGE_SIZE = 10;

// ฟิลด์โครงการที่มาจาก SAP (ไฟล์ 16_project_Template.xlsx) - แก้มือได้จากหน้านี้เช่นกัน
const SAP_TEXT_FIELDS = ["owner_code", "owner_name", "project_manager", "cost_center", "description"];
const SAP_DATE_FIELDS = ["planned_start_date", "planned_finish_date", "actual_start_date", "actual_finish_date"];
const EMPTY_SAP_FIELDS = [...SAP_TEXT_FIELDS, ...SAP_DATE_FIELDS].reduce((acc, key) => ({ ...acc, [key]: "" }), {});

// สถานะดำเนินการ - ค่าตรงกับ backend (project.constants.js) แปลด้วย key processing_status_<ค่า>
// ค่าจากไฟล์ SAP ถูก backend แปลงให้เหลือ 3 ค่านี้ตอนนำเข้าแล้ว (ว่าง = not_started)
const PROCESSING_STATUSES = ["not_started", "in_progress", "completed"];
const DEFAULT_PROCESSING_STATUS = "not_started";
const processingStatusOptions = () =>
  PROCESSING_STATUSES.map((value) => ({
    value,
    label: t(`processing_status_${value}`),
  }));
const normalizeProcessingStatus = (value) => (PROCESSING_STATUSES.includes(value) ? value : DEFAULT_PROCESSING_STATUS);
const processingStatusLabel = (value) => t(`processing_status_${normalizeProcessingStatus(value)}`);
// สีป้ายสถานะดำเนินการในตาราง: ยังไม่เริ่ม = เทา / อยู่ระหว่างดำเนินการ = น้ำเงิน / เสร็จสิ้น = เขียว
const PROCESSING_STATUS_STYLE = {
  not_started: "text-secondary bg-secondary-light",
  in_progress: "text-primary bg-primary-light",
  completed: "text-success bg-success-light",
};

// สถานะโครงการ 3 ค่า (แทน is_active เดิม) - ค่าตรงกับ backend แปลด้วย key i18n ชื่อเดียวกับค่า
//   active = ใช้งาน / inactive = ปิดใช้งาน / hold = ระงับชั่วคราว (ลำดับนี้ = ลำดับในตัวเลือกทุกจุด ระงับชั่วคราวไว้ท้ายสุด)
const PROJECT_STATUSES = ["active", "inactive", "hold"];
const DEFAULT_PROJECT_STATUS = "active";
const PROJECT_STATUS_STYLE = {
  active: "text-success bg-success-light",
  hold: "text-warning bg-warning-light",
  inactive: "text-secondary bg-light border",
};
// color = สีของ label/radio ตอนถูกเลือก (ตรงกับสีป้ายสถานะในตาราง)
const PROJECT_STATUS_COLOR = { active: "success", inactive: "secondary", hold: "warning" };
const projectStatusOptions = () => PROJECT_STATUSES.map((value) => ({ value, label: t(value), color: PROJECT_STATUS_COLOR[value] }));
// แถวเก่าที่ backend ยังไม่ได้ migrate จะไม่มี status -> เดาจาก is_active
const projectStatusOf = (item) => (PROJECT_STATUSES.includes(item?.status) ? item.status : item?.is_active === false ? "inactive" : DEFAULT_PROJECT_STATUS);

// process ของงาน - ค่าตรงกับ backend (modules/admin/project/project.constants.js) แปลด้วย key project_type_<ค่า>
// ใช้เป็น fallback แสดงผลแถวเก่าที่ยังไม่ได้ผูกประเภทโครงการจาก master
const PROJECT_TYPES = ["pool_maintenance", "installation"];
const isEN = () => localStorage.getItem("lang") === "EN";
// ประเภทงานของโครงการเลือกจาก master "ประเภทโครงการ" (เมนูตั้งค่าคำขอบริการ) - ชื่อตามภาษาที่ใช้อยู่
const projectTypeName = (projectType) => (projectType ? (isEN() ? projectType.name_en || projectType.name_th : projectType.name_th) || "-" : "-");
// ป้ายชื่อช่าง: รหัส - ชื่อ นามสกุล ตามภาษาที่ใช้อยู่ (ไม่ต่อท้ายชื่อเล่น/แผนก)
const technicianName = (technician) => {
  const en = isEN();
  const first = en ? technician.first_name_en || technician.first_name_th : technician.first_name_th || technician.first_name_en;
  const last = en ? technician.last_name_en || technician.last_name_th : technician.last_name_th || technician.last_name_en;
  const nick = en ? technician.nickname_en || technician.nickname_th : technician.nickname_th || technician.nickname_en;
  const name = [first, last].filter(Boolean).join(" ") || nick || "";
  return [technician.technician_code, name].filter(Boolean).join(" - ") || technician.uid;
};
// แถวเก่า / โครงการที่ถูกสร้างอ้อมจากการนำเข้า WBS อาจยังไม่มีประเภท -> แสดง "-"
const projectTypeLabel = (value) => (PROJECT_TYPES.includes(value) ? t(`project_type_${value}`) : "-");
const projectTypeCell = (item) => (item.project_type_master ? projectTypeName(item.project_type_master) : projectTypeLabel(item.project_type));

// ตัวเลือกเจ้าของโครงการ = ลูกค้าจาก master: value = รหัสลูกค้า (เก็บลง owner_code) ใน dropdown แสดง รหัส + ชื่อ แต่เลือกแล้วแสดงแค่รหัส
const customerDisplayName = (c) => (isEN() ? c.full_name_en || c.full_name_th : c.full_name_th || c.full_name_en) || "";
const toOwnerOption = (c) => {
  const name = customerDisplayName(c);
  return { value: c.customer_code, label: [c.customer_code, name].filter(Boolean).join(" "), selected_label: c.customer_code, name };
};

// yyyy-mm-dd -> dd/mm/yyyy สำหรับแสดงในตาราง
const formatDate = (value) => {
  const m = String(value ?? "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};

export default class AdminProjects extends Component {
  constructor(props) {
    super(props);
    this.state = {
      active_tab: TAB_PROJECT,

      // ---- Tab: Projects ----
      filter_search: "",
      filter_status: "", // "" = ทุกสถานะ (active / hold / inactive)
      filter_department: "", // "" = ทุกแผนก (department_code = projects.cost_center)
      data_filter_departments: [], // แผนกในสิทธิ์ของผู้ใช้ (scope_department) สำหรับตัวกรอง
      page: 1,
      count: 0,
      data: [],
      // KPI card ด้านบน - นับทั้งบริษัทจาก backend (summary=true) ไม่ใช่เฉพาะหน้าปัจจุบัน
      summary_count: {
        total: 0,
        active: 0,
        inactive: 0,
        hold: 0,
        unassigned: 0,
      },

      modal_create: false,
      modal_update: false,
      // ยืนยันก่อนเปลี่ยนสถานะเป็น hold จาก select ในตาราง (hold_target = แถวที่กำลังจะระงับ)
      modal_hold: false,
      hold_target: null,

      // Form state (modal)
      uid: "",
      company_id: company?.id,
      project_code: "",
      name: "",
      project_type_id: "", // uid ของประเภทโครงการจาก master
      technician_ids: [], // uid ช่างผู้รับผิดชอบ (เลือกได้หลายคน)
      ...EMPTY_SAP_FIELDS,
      processing_status: DEFAULT_PROCESSING_STATUS,
      // ข้อมูลจาก SAP ที่แสดงอย่างเดียว (มากับไฟล์นำเข้า)
      sap_created_by: "",
      sap_created_on: "",
      controlling_area: "",
      profit_center: "",
      status: DEFAULT_PROJECT_STATUS,

      // master ประเภทโครงการ (รวมที่ปิดใช้งาน เพื่อแสดงชื่อของแถวเก่าได้) และช่างที่เลือกได้ตามประเภทที่เลือก
      data_project_type: [],
      data_technician: [],
      data_departments: [], // แผนกสำหรับช่อง Cost Center (value = department_code)
      data_owner_customers: [], // ผลค้นหาลูกค้า (server) สำหรับช่องรหัสเจ้าของโครงการ
      technician_loading: false,
      tech_dropdown_open: false,
      tech_search: "",
      tech_pos: null, // ตำแหน่ง dropdown (fixed) คำนวณจากช่องเลือกตอนเปิด
      tech_order: [], // ลำดับ uid ช่างในรายการ ล็อกตอนเปิด dropdown (คนที่เลือกแล้วอยู่บน)

      // ---- Tab: WBS ----
      wbs_filter_search: "",
      wbs_filter_project_id: "",
      wbs_page: 1,
      wbs_count: 0,
      data_wbs: [],
      data_project: [], // ตัวเลือกโครงการ (filter + modal ของ WBS)

      modal_wbs_create: false,
      modal_wbs_update: false,

      wbs_uid: "",
      wbs_project_id: "",
      wbs_element: "",
      wbs_element_name: "",
      entity_guid: "",
      wbs_is_active: "Active",
    };
  }

  techTriggerRef = React.createRef();

  async componentDidMount() {
    loading(false);
    this.Get(1);
    this.GetProjectTypes();
    this.GetDepartments();
    this.GetFilterDepartments();
    // ลิงก์จากหน้ารายงานใกล้หมดประกัน: ?uid=<project uid> -> เปิดฟอร์มโครงการนั้นทันที
    const deepUid = new URLSearchParams(window.location.search).get("uid");
    // เอา ?uid= ออกจาก URL (คง param อื่น) กัน popup เด้งกลับตอนรีเฟรช
    if (deepUid) {
      const url = new URL(window.location.href);
      url.searchParams.delete("uid");
      window.history.replaceState({}, "", url.pathname + url.search + url.hash);
    }
    if (deepUid) {
      // โครงการนอกสิทธิ์การเข้าถึง (Department Access) backend ตอบ 403 "Access denied" -> แจ้งว่าไม่มีสิทธิ์เข้าถึงข้อมูลนี้
      const result = await GET("admin/projects", { uid: deepUid }, true, { skipAccessDeniedAlert: true });
      if (result?.status && result.data) this.openEditModal(result.data);
      else if (result?.message === "Access denied") alert("", "warning", t("no_permission"), t("project_no_access"));
    }
  }

  // ค้นหาลูกค้าฝั่ง server (รหัส + ชื่อ) - ลูกค้าที่ไม่มีรหัสเลือกไม่ได้ เพราะ owner_code ผูกกับรหัสลูกค้า
  ownerSearchToken = 0;
  SearchOwnerCustomers = async (keyword = "") => {
    const token = ++this.ownerSearchToken;
    const rows = await searchCustomerRows(keyword, { search_scope: "display" });
    if (token !== this.ownerSearchToken || rows === null) return;
    const seen = new Set();
    const options = rows.filter((c) => c.customer_code && !seen.has(c.customer_code) && seen.add(c.customer_code)).map(toOwnerOption);
    this.setState({ data_owner_customers: options });
  };

  // ชื่อเจ้าของโครงการเติมอัตโนมัติจากลูกค้าที่เลือก (ช่องชื่อ disable ไว้)
  onOwnerChange = (value, item) => this.setState({ owner_code: value || "", owner_name: value ? item?.name || "" : "" });

  // ตัวกรองแผนกหน้า list: เฉพาะแผนกที่ผู้ใช้มีสิทธิ์ (Executive เห็นทั้งหมด)
  GetFilterDepartments = async () => {
    const result = await GET("admin/departments", { page_size: 1000, page: 0, data_search: "", scope_department: "true" });
    if (result?.status && Array.isArray(result.data)) this.setState({ data_filter_departments: result.data });
  };

  filterDepartmentOptions = () =>
    this.state.data_filter_departments
      .filter((d) => d.department_code)
      .map((d) => ({ value: d.department_code, label: [d.department_code, d.name].filter(Boolean).join(" - ") }));

  GetDepartments = async () => {
    const result = await GET("admin/departments", { page_size: 1000, page: 0, data_search: "" });
    if (result?.status && Array.isArray(result.data)) this.setState({ data_departments: result.data });
  };

  // ตัวเลือก Cost Center = แผนกที่ใช้งานอยู่ + แผนกเดิมของโครงการที่กำลังแก้ (แม้ถูกปิด หรือรหัสไม่อยู่ในรายการ)
  costCenterOptions = () => {
    const current = this.state.cost_center;
    const options = this.state.data_departments
      .filter((d) => d.department_code && (d.is_active !== false || d.department_code === current))
      .map((d) => ({ value: d.department_code, label: [d.department_code, d.name].filter(Boolean).join(" - ") }));
    if (current && !options.some((o) => o.value === current)) options.push({ value: current, label: current });
    return options;
  };

  // ประเภทโครงการจาก master (แท็บ "ประเภทโครงการ" ในตั้งค่าคำขอบริการ) - โหลดครั้งเดียว
  GetProjectTypes = async () => {
    const result = await GET("admin/project-types", {
      page_size: 10000,
      page: 0,
      data_search: "",
    });
    if (!result?.status) return;
    // ถ้าเปิดฟอร์มแก้ไขค้างไว้ก่อน master โหลดเสร็จ ให้โหลดรายชื่อช่างต่อเมื่อรู้แผนกของประเภทแล้ว
    this.setState({ data_project_type: result.data || [] }, () => this.state.project_type_id && this.GetTechnicians());
  };

  // ตัวเลือกประเภทงาน: เฉพาะที่เปิดใช้งาน + ประเภทเดิมของโครงการที่กำลังแก้ (แม้ถูกปิดไปแล้ว) เพื่อไม่บังคับให้เปลี่ยน
  projectTypeOptions = () =>
    this.state.data_project_type
      .filter((pt) => pt.is_active !== false || pt.uid === this.state.project_type_id)
      .map((pt) => ({
        value: pt.uid,
        label: pt.is_active === false ? `${projectTypeName(pt)} (${t("inactive")})` : projectTypeName(pt),
      }));

  // แผนกของประเภทโครงการที่เลือก -> ใช้กรองช่างที่มีสิทธิ์เข้าถึงแผนกเหล่านั้น
  selectedProjectTypeDepartments = () => {
    const pt = this.state.data_project_type.find((e) => e.uid === this.state.project_type_id);
    return (pt?.departments || []).map((d) => d.uid).filter(Boolean);
  };

  onProjectTypeChange = (value) => {
    this.setState(
      {
        project_type_id: value || "",
        tech_dropdown_open: false,
        tech_search: "",
      },
      this.GetTechnicians,
    );
  };

  // ช่างที่เลือกได้ = ช่างที่ใช้งานอยู่และได้รับ department access ตรงกับแผนกของประเภทงานที่เลือก
  // โหลดเสร็จแล้วตัดคนที่เลือกไว้แต่ไม่อยู่ในรายการออก (เช่น เปลี่ยนประเภทงานแล้วช่างคนเดิมไม่มีสิทธิ์แผนกใหม่)
  GetTechnicians = async () => {
    // master ยังไม่มา = ยังไม่รู้แผนก อย่าเพิ่งล้างรายชื่อที่เลือกไว้ (GetProjectTypes จะเรียกซ้ำให้เมื่อโหลดเสร็จ)
    if (!this.state.data_project_type.length) return;
    const departmentUids = this.selectedProjectTypeDepartments();
    if (!this.state.project_type_id || !departmentUids.length) {
      this.setState({ data_technician: [], technician_ids: [] });
      return;
    }
    const requestedType = this.state.project_type_id;
    this.setState({ technician_loading: true });
    const result = await GET("admin/technicians", {
      page: 0,
      page_size: 10000,
      lite: "true",
      is_active: "true",
      department_ids: departmentUids.join(","),
    });
    // ผู้ใช้เปลี่ยนประเภทไปแล้วระหว่างรอ = ทิ้งผลลัพธ์ชุดนี้
    if (this.state.project_type_id !== requestedType) return;
    const technicians = result?.status ? result.data || [] : [];
    const allowed = new Set(technicians.map((e) => e.uid));
    this.setState({
      data_technician: technicians,
      technician_loading: false,
      technician_ids: result?.status ? this.state.technician_ids.filter((uid) => allowed.has(uid)) : this.state.technician_ids,
    });
  };

  toggleTechDropdown = () => {
    if (this.state.tech_dropdown_open) {
      this.setState({
        tech_dropdown_open: false,
        tech_search: "",
        tech_pos: null,
      });
      return;
    }
    if (!this.state.project_type_id) return;
    const rect = this.techTriggerRef.current?.getBoundingClientRect();
    let pos = null;
    if (rect) {
      const gap = 4;
      const margin = 12;
      const spaceBelow = window.innerHeight - rect.bottom - gap - margin;
      const spaceAbove = rect.top - gap - margin;
      const flipUp = spaceBelow < 240 && spaceAbove > spaceBelow;
      pos = {
        left: rect.left,
        width: rect.width,
        top: flipUp ? undefined : rect.bottom + gap,
        bottom: flipUp ? window.innerHeight - rect.top + gap : undefined,
        maxHeight: Math.max(160, Math.min(360, flipUp ? spaceAbove : spaceBelow)),
      };
    }
    // ลำดับรายการล็อกไว้ตอนเปิด (คนที่เลือกแล้วขึ้นบน) - ติ๊กเพิ่ม/เอาออกระหว่างเปิดจะไม่สลับตำแหน่ง ไม่งั้นเลื่อนหาช่างต่อไม่สะดวก
    const selected = new Set(this.state.technician_ids);
    const tech_order = [
      ...this.state.data_technician.filter((e) => selected.has(e.uid)),
      ...this.state.data_technician.filter((e) => !selected.has(e.uid)),
    ].map((e) => e.uid);
    this.setState({ tech_dropdown_open: true, tech_search: "", tech_pos: pos, tech_order });
  };

  toggleTechnician = (uid) => {
    this.setState((prev) => ({
      technician_ids: prev.technician_ids.includes(uid) ? prev.technician_ids.filter((e) => e !== uid) : [...prev.technician_ids, uid],
    }));
  };

  // รายการใน dropdown: คนที่เลือกแล้วขึ้นบนสุด (คงลำดับเดิมภายในกลุ่ม) แล้วค่อยกรองตามคำค้น
  // รายการใน dropdown: ใช้ลำดับที่ล็อกไว้ตอนเปิด (tech_order) แล้วกรองตามคำค้น
  filterTechnicians = () => {
    const search = (this.state.tech_search || "").toLowerCase();
    const rank = new Map((this.state.tech_order || []).map((uid, index) => [uid, index]));
    return this.state.data_technician
      .filter((e) => !search || technicianName(e).toLowerCase().includes(search))
      .sort((a, b) => (rank.get(a.uid) ?? Infinity) - (rank.get(b.uid) ?? Infinity));
  };

  // ============================== Projects ==============================
  Get = async (page) => {
    const { filter_search, filter_status, filter_department } = this.state;
    loading(true);

    const [result, result_summary] = await Promise.all([
      GET("admin/projects", {
        page_size: PAGE_SIZE,
        page,
        data_search: filter_search,
        status: filter_status || "",
        cost_center: filter_department || "",
      }),
      GET("admin/projects", { summary: "true", cost_center: filter_department || "" }),
    ]);

    if (result?.status) {
      this.setState({
        data: result.data || [],
        count: result.pagination?.count || 0,
        page,
      });
    }
    if (result_summary?.status && result_summary.data) {
      this.setState({
        summary_count: { ...this.state.summary_count, ...result_summary.data },
      });
    }
    loading(false);
  };

  resetForm = () => {
    this.setState({
      uid: "",
      project_code: "",
      name: "",
      project_type_id: "",
      technician_ids: [],
      data_technician: [],
      tech_dropdown_open: false,
      tech_search: "",
      ...EMPTY_SAP_FIELDS,
      processing_status: DEFAULT_PROCESSING_STATUS,
      sap_created_by: "",
      sap_created_on: "",
      controlling_area: "",
      profit_center: "",
      status: DEFAULT_PROJECT_STATUS,
    });
  };

  openCreateModal = () => {
    this.resetForm();
    this.setState({ modal_create: true, modal_update: false });
  };

  openEditModal = (item) => {
    this.setState(
      {
        modal_create: false,
        modal_update: true,
        uid: item.uid,
        project_code: item.project_code || "",
        name: item.name || "",
        project_type_id: item.project_type_id || "",
        technician_ids: item.technician_ids || (item.technicians || []).map((e) => e.uid),
        data_technician: [],
        tech_dropdown_open: false,
        tech_search: "",
        ...[...SAP_TEXT_FIELDS, ...SAP_DATE_FIELDS].reduce((acc, key) => ({ ...acc, [key]: item[key] || "" }), {}),
        processing_status: PROCESSING_STATUSES.includes(item.processing_status) ? item.processing_status : DEFAULT_PROCESSING_STATUS,
        sap_created_by: item.sap_created_by || "",
        sap_created_on: item.sap_created_on || "",
        controlling_area: item.controlling_area || "",
        profit_center: item.profit_center || "",
        status: projectStatusOf(item),
      },
      () => {
        this.GetTechnicians();
        this.fillOwnerNameIfMissing(item.owner_code, item.owner_name);
      },
    );
  };

  // มี owner_code แต่ไม่มีชื่อ (เช่น นำเข้าจากไฟล์ที่ใส่แต่ Owner ID) -> lookup ชื่อลูกค้าจาก master มาเติม
  fillOwnerNameIfMissing = async (ownerCode, ownerName) => {
    const code = String(ownerCode || "").trim();
    if (!code || String(ownerName || "").trim()) return;
    const rows = await searchCustomerRows(code, { search_scope: "display" });
    const found = (rows || []).find((c) => c.customer_code === code);
    if (found && this.state.owner_code === code && !this.state.owner_name) {
      this.setState({ owner_name: customerDisplayName(found), data_owner_customers: [toOwnerOption(found)] });
    }
  };

  CreateUpdate = async () => {
    const {
      uid,
      company_id,
      project_code,
      name,
      project_type_id,
      technician_ids,
      planned_start_date,
      planned_finish_date,
      processing_status,
      cost_center,
      status,
    } = this.state;

    if (!project_type_id) {
      alert("", "warning", t("warning"), t("verify_project_type"));
      return;
    } else if (!project_code.trim()) {
      alert("", "warning", t("warning"), t("verify_project_code"));
      return;
    } else if (!name.trim()) {
      alert("", "warning", t("warning"), t("verify_project_name"));
      return;
    } else if (!planned_start_date) {
      alert("", "warning", t("warning"), t("verify_planned_start_date"));
      return;
    } else if (!planned_finish_date) {
      alert("", "warning", t("warning"), t("verify_planned_finish_date"));
      return;
    } else if (planned_finish_date < planned_start_date) {
      // yyyy-mm-dd เทียบเป็นข้อความได้ตรง ๆ
      alert("", "warning", t("warning"), t("verify_planned_period"));
      return;
    } else if (!PROCESSING_STATUSES.includes(processing_status)) {
      alert("", "warning", t("warning"), t("verify_processing_status"));
      return;
    } else if (!String(cost_center || "").trim()) {
      alert("", "warning", t("warning"), t("verify_cost_center"));
      return;
    }

    if (!(await alert("", "info", t("confirm"), t("confirm_submission"), t("confirm"), t("cancel")))) {
      return;
    }

    loading(true);
    const body = {
      company_id,
      project_code: project_code.trim(),
      name: name.trim(),
      project_type_id,
      technician_ids,
      ...SAP_TEXT_FIELDS.reduce((acc, key) => ({ ...acc, [key]: String(this.state[key] || "").trim() }), {}),
      ...SAP_DATE_FIELDS.reduce((acc, key) => ({ ...acc, [key]: this.state[key] || "" }), {}),
      processing_status,
      status,
    };

    const result = !uid ? await POST("admin/projects", body) : await PUT("admin/projects/" + uid, body);

    if (result?.status === true) {
      alert("#", "success");
      this.setState({
        modal_create: false,
        modal_update: false,
        data_project: [],
      });
      this.Get(this.state.page);
    } else {
      alert("", "warning", t("warning"), result?.message || t("error_occurred"));
    }
    loading(false);
  };

  // เปลี่ยนสถานะจาก select ในตาราง - hold ยืนยันใน modal เฉพาะ ส่วน active / inactive ยืนยันผ่าน alert ก่อนบันทึก
  changeStatus = async (item, status) => {
    if (!PROJECT_STATUSES.includes(status) || status === projectStatusOf(item)) return;
    if (status === "hold") {
      this.setState({ modal_hold: true, hold_target: item });
      return;
    }
    const detail = t("confirm_project_status_change_desc")
      .replace("{name}", item.name || item.project_code || "-")
      .replace("{status}", t(status));
    if (!(await alert("", "info", t("confirm_status_change_title"), detail, t("confirm"), t("cancel")))) return;
    this.UpdateStatus(item, status);
  };

  closeHoldModal = () => this.setState({ modal_hold: false, hold_target: null });

  confirmHold = async () => {
    const target = this.state.hold_target;
    this.closeHoldModal();
    if (target) await this.UpdateStatus(target, "hold");
  };

  UpdateStatus = async (item, status) => {
    loading(true);
    const result = await PUT("admin/projects/" + item.uid, { status });
    if (result?.status === true) {
      // อัปเดตแถวในตารางเลย ไม่ต้อง reload ทั้งหน้า (ตัวเลือกโครงการของแท็บ WBS ให้โหลดใหม่)
      const previous = projectStatusOf(item);
      const summary_count = { ...this.state.summary_count };
      summary_count[previous] = Math.max(0, (summary_count[previous] || 0) - 1);
      summary_count[status] = (summary_count[status] || 0) + 1;
      this.setState({
        data: this.state.data.map((row) => (row.uid === item.uid ? { ...row, status, is_active: status !== "inactive" } : row)),
        data_project: [],
        summary_count,
      });
      alert("", "success", t("success"), t("project_status_updated"));
    } else {
      alert("", "warning", t("warning"), result?.message || t("error_occurred"));
    }
    loading(false);
  };

  Delete = async (uid) => {
    // ลบโครงการ = ลบ WBS ใต้โครงการนั้นด้วย จึงเตือนให้ชัด
    if (!(await alert("", "warning", t("delete_item"), t("delete_project_with_wbs_desc"), t("confirm"), t("cancel")))) {
      return;
    }
    loading(true);
    const result = await DELETE("admin/projects/" + uid, null);
    if (result?.status) {
      alert("#", "success");
      this.setState({ data_project: [] });
      this.Get(this.state.page);
    } else {
      alert("", "warning", t("warning"), result?.message || t("error_occurred"));
    }
    loading(false);
  };

  // ============================== WBS ==============================
  switchTab = (tab) => {
    if (tab === this.state.active_tab) return;
    this.setState({ active_tab: tab }, () => {
      if (tab === TAB_WBS) {
        this.GetProjectOptions();
        this.GetWbs(1);
      } else {
        this.Get(this.state.page);
      }
    });
  };

  // กดจำนวน WBS ในตารางโครงการ -> ไปแท็บ WBS โดยกรองเฉพาะโครงการนั้น
  openWbsOfProject = (project) => {
    this.setState(
      {
        active_tab: TAB_WBS,
        wbs_filter_project_id: project.uid,
        wbs_filter_search: "",
      },
      () => {
        this.GetProjectOptions();
        this.GetWbs(1);
      },
    );
  };

  // โหลดครั้งเดียว (ล้างทิ้งเมื่อมีการเพิ่ม/แก้/ลบโครงการ)
  GetProjectOptions = async () => {
    if (this.state.data_project.length) return;
    const result = await GET("admin/projects", {
      page_size: 10000,
      page: 0,
      data_search: "",
    });
    if (!result?.status) return;
    this.setState({
      data_project: (result.data || []).map((e) => ({
        value: e.uid,
        label: [e.project_code, e.name].filter(Boolean).join(" "),
      })),
    });
  };

  GetWbs = async (page) => {
    const { wbs_filter_search, wbs_filter_project_id } = this.state;
    loading(true);

    let result = await GET("admin/wbs", {
      page_size: PAGE_SIZE,
      page,
      data_search: wbs_filter_search,
      project_id: wbs_filter_project_id || "",
    });

    if (result?.status) {
      this.setState({
        data_wbs: result.data || [],
        wbs_count: result.pagination?.count || 0,
        wbs_page: page,
      });
    }
    loading(false);
  };

  openWbsCreateModal = () => {
    this.setState({
      modal_wbs_create: true,
      modal_wbs_update: false,
      wbs_uid: "",
      // กรองโครงการอยู่ = ตั้งใจเพิ่ม WBS ให้โครงการนั้น
      wbs_project_id: this.state.wbs_filter_project_id || "",
      wbs_element: "",
      wbs_element_name: "",
      entity_guid: "",
      wbs_is_active: "Active",
    });
  };

  openWbsEditModal = (item) => {
    this.setState({
      modal_wbs_create: false,
      modal_wbs_update: true,
      wbs_uid: item.uid,
      wbs_project_id: item.project?.uid || "",
      wbs_element: item.wbs_element || "",
      wbs_element_name: item.wbs_element_name || "",
      entity_guid: item.entity_guid || "",
      wbs_is_active: item.is_active ? "Active" : "Inactive",
    });
  };

  CreateUpdateWbs = async () => {
    const { wbs_uid, company_id, wbs_project_id, wbs_element, wbs_element_name, entity_guid, wbs_is_active } = this.state;

    if (!wbs_project_id) {
      alert("", "warning", t("warning"), t("verify_wbs_project"));
      return;
    } else if (!wbs_element.trim()) {
      alert("", "warning", t("warning"), t("verify_wbs_element"));
      return;
    }

    if (!(await alert("", "info", t("confirm"), t("confirm_submission"), t("confirm"), t("cancel")))) {
      return;
    }

    loading(true);
    const body = {
      company_id,
      project_id: wbs_project_id,
      wbs_element: wbs_element.trim(),
      wbs_element_name: wbs_element_name.trim(),
      entity_guid: entity_guid.trim(),
      is_active: wbs_is_active === "Active",
    };

    const result = !wbs_uid ? await POST("admin/wbs", body) : await PUT("admin/wbs/" + wbs_uid, body);

    if (result?.status === true) {
      alert("#", "success");
      this.setState({ modal_wbs_create: false, modal_wbs_update: false });
      this.GetWbs(this.state.wbs_page);
    } else {
      alert("", "warning", t("warning"), result?.message || t("error_occurred"));
    }
    loading(false);
  };

  DeleteWbs = async (uid) => {
    if (!(await alert("", "warning", t("delete_item"), t("delete_desc"), t("confirm"), t("cancel")))) {
      return;
    }
    loading(true);
    const result = await DELETE("admin/wbs/" + uid, null);
    if (result?.status) {
      alert("#", "success");
      this.GetWbs(this.state.wbs_page);
    } else {
      alert("", "warning", t("warning"), result?.message || t("error_occurred"));
    }
    loading(false);
  };

  // ============================== Render ==============================
  // WBS ยังเป็น 2 สถานะ (is_active) ส่วนโครงการใช้ renderProjectStatusSelect (3 สถานะ)
  renderStatusBadge = (isActive) =>
    isActive ? (
      <span className="text-success text-12 fw-bold bg-success-light px-2 py-1 rounded-pill">{t("active")}</span>
    ) : (
      <span className="text-secondary text-12 fw-bold bg-light px-2 py-1 rounded-pill border">{t("inactive")}</span>
    );

  // select สถานะโครงการในตาราง - เลือกแล้วบันทึกทันที (hold ยืนยันผ่าน modal ก่อน) สีช่องตามสถานะปัจจุบัน
  renderProjectStatusSelect = (item) => {
    const status = projectStatusOf(item);
    return (
      <div className="mx-auto wpx-100">
        <Select
          className={`form-control form-control-sm rounded-pill text-12 ${PROJECT_STATUS_STYLE[status]}`}
          options={projectStatusOptions()}
          value={status}
          disabled={!can("projects.update")}
          onChange={(value) => this.changeStatus(item, value)}
        />
      </div>
    );
  };

  // options = [{ value, label }] - โครงการ 3 สถานะ / WBS 2 สถานะ
  // options = [{ value, label, color? }] - color ใช้ย้อมสี radio/label ตอนถูกเลือก / note = เนื้อหาเพิ่มใต้ตัวเลือก (เช่น คำเตือนตอนระงับชั่วคราว)
  renderStatusRadio = (name, value, onChange, options, desc = t("status_desc"), note = null) => (
    <div className="col-12 mt-4 border-top pt-4">
      <h6 className="fw-bold mb-1 d-flex align-items-center">
        <span className="icon text-primary me-2">{""}</span> {t("status")}
      </h6>
      <small className="text-muted d-block mb-3">{desc}</small>
      <div className="d-flex gap-4 flex-wrap">
        {options.map((option) => {
          const checked = value === option.value;
          const color = checked && option.color ? `text-${option.color}` : "text-dark";
          return (
            <div key={option.value} className="form-check d-flex align-items-center gap-2">
              <input
                className="form-check-input mt-0 pointer"
                type="radio"
                name={name}
                id={`${name}${option.value}`}
                checked={checked}
                onChange={() => onChange(option.value)}
                style={checked && option.color ? { backgroundColor: `var(--bs-${option.color})`, borderColor: `var(--bs-${option.color})` } : undefined}
              />
              <label className={`form-check-label text-14 pointer ${color} ${checked ? "fw-bold" : ""}`} htmlFor={`${name}${option.value}`}>
                {option.label}
              </label>
            </div>
          );
        })}
      </div>
      {note}
    </div>
  );

  // คำเตือนผลของการระงับชั่วคราว - ใช้ทั้งใน modal ยืนยัน (ตาราง) และใต้ radio ในฟอร์ม
  renderHoldEffects = (className = "") => (
    <div className={`bg-warning-light rounded-3 px-3 py-3 text-start ${className}`}>
      <div className="text-14 fw-bold text-dark mb-2">{t("hold_project_desc")}</div>
      <ul className="text-14 text-dark mb-0 ps-3">
        <li>{t("hold_project_effect_plan")}</li>
        <li>{t("hold_project_effect_calendar")}</li>
        <li>{t("hold_project_effect_checkin")}</li>
      </ul>
    </div>
  );

  // การ์ดสรุปจำนวนด้านบนแท็บโครงการ
  renderSummaryCards() {
    const { summary_count } = this.state;
    const cards = [
      {
        key: "total",
        label: t("total_projects"),
        icon: "\uf1ad",
        color: "secondary",
        bg: "bg-light",
        text: "text-dark",
      },
      {
        key: "active",
        label: t("active_projects"),
        icon: "\uf058",
        color: "success",
        bg: "bg-success-light",
        text: "text-success",
      },
      {
        key: "inactive",
        label: t("inactive_projects"),
        icon: "\uf056",
        color: "secondary",
        bg: "bg-secondary-light",
        text: "text-secondary",
      },
      {
        key: "hold",
        label: t("hold_projects"),
        icon: "\uf04c",
        color: "warning",
        bg: "bg-warning-light",
        text: "text-warning",
      },
      {
        key: "unassigned",
        label: t("projects_without_technician"),
        icon: "\uf0c0",
        color: "danger",
        bg: "bg-danger-light",
        text: "text-danger",
      },
    ];
    return (
      <div className="row g-3 mb-4">
        {cards.map((card) => (
          <div key={card.key} className="col-xl col-md-4 col-6">
            <div className="card border-0 rounded-4 shadow-sm p-3 h-100">
              <div className="d-flex align-items-center">
                <div className={`wpx-40 hpx-40 me-3 rounded ${card.bg} text-${card.color} d-flex align-items-center justify-content-center text-20 icon`}>
                  {card.icon}
                </div>
                <div>
                  <h4 className={`fw-bold ${card.text} mb-0`}>{formatNumber(summary_count[card.key] ?? 0)}</h4>
                  <div className="text-secondary text-12">{card.label}</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  renderProjectTab() {
    const { data, count, page, filter_search, filter_status, filter_department } = this.state;

    return (
      <>
        {this.renderSummaryCards()}

        {/* Filter Toolbar */}
        <div className="bg-white rounded-4 shadow-sm p-4 mb-4">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div className="d-flex flex-wrap gap-2 flex-grow-1">
              <div className="position-relative">
                <span className="icon position-absolute top-50 start-0 translate-middle-y ms-3 text-secondary text-12">{""}</span>
                <input
                  type="text"
                  className="form-control ps-5 wpx-350"
                  style={{ height: "38px" }}
                  placeholder={t("search_projects")}
                  value={filter_search}
                  onChange={(e) => this.setState({ filter_search: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && this.Get(1)}
                />
              </div>
              <div className="wpx-200" style={{ height: "38px" }}>
                <Select
                  className="form-control h-100"
                  options={this.filterDepartmentOptions()}
                  value={filter_department}
                  placeholder={t("at_all_departments")}
                  defaultLabel={t("at_all_departments")}
                  onChange={(value) => this.setState({ filter_department: value || "" })}
                />
              </div>
              {/* สูง 38px เท่าช่องค้นหา - Select ส่ง className ให้ทั้งกล่องนอกและ input ใน จึงใช้ h-100 อิงความสูงจาก wrapper */}
              <div className="wpx-200" style={{ height: "38px" }}>
                <Select
                  className="form-control h-100"
                  options={projectStatusOptions()}
                  value={filter_status}
                  placeholder={t("all_statuses")}
                  defaultLabel={t("all_statuses")}
                  onChange={(value) => this.setState({ filter_status: value || "" })}
                />
              </div>
              <div className="d-flex gap-2">
                <button className="btn btn-secondary px-3 py-2 fw-bold rounded-3 shadow-sm" onClick={() => this.Get(1)}>
                  {t("filter")}
                </button>
                <button
                  className="btn btn-white border px-3 py-2 fw-bold rounded-3 shadow-sm"
                  onClick={() => this.setState({ filter_search: "", filter_status: "", filter_department: "" }, () => this.Get(1))}
                >
                  {t("reset")}
                </button>
              </div>
            </div>
            {can("projects.create") ? (
            <button className="btn btn-primary px-3 py-2 fw-bold rounded-3 shadow-sm d-flex align-items-center" onClick={this.openCreateModal}>
              <span className="me-2 text-16">+</span> {t("add_project")}
            </button>
            ) : null}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-4 shadow-sm mb-4 overflow-hidden">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0" style={{ minWidth: "1100px" }}>
              <thead>
                <tr className="text-dark fw-bold text-12 border-bottom border-light">
                  <th className="py-3 border-0">{t("project_code")}</th>
                  <th className="py-3 border-0">{t("project_name")}</th>
                  <th className="py-3 border-0">{t("project_type")}</th>
                  <th className="py-3 border-0">{t("project_planned_period")}</th>
                  <th className="py-3 border-0">{t("processing_status")}</th>
                  <th className="py-3 border-0 text-center">{t("wbs_count")}</th>
                  <th className="py-3 border-0 text-center">{t("status")}</th>
                  <th className="py-3 border-0 text-center">{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center text-secondary py-5">
                      {t("no_data")}
                    </td>
                  </tr>
                ) : (
                  data.map((item, idx) => (
                    <tr key={idx} className="border-bottom border-light bg-white">
                      <td>
                        <div className="text-secondary text-14">{item.project_code}</div>
                      </td>
                      <td>
                        <div className="text-secondary text-14 text-truncate" style={{ maxWidth: "300px" }} title={item.name}>
                          {item.name}
                        </div>
                      </td>
                      <td>
                        <div className="text-secondary text-14 text-nowrap">{projectTypeCell(item)}</div>
                      </td>
                      <td>
                        <div className="text-secondary text-14 text-nowrap">
                          {[formatDate(item.planned_start_date), formatDate(item.planned_finish_date)].filter(Boolean).join(" - ") || "-"}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`text-12 fw-bold px-3 py-1 rounded-pill text-nowrap ${PROCESSING_STATUS_STYLE[normalizeProcessingStatus(item.processing_status)]}`}
                        >
                          {processingStatusLabel(item.processing_status)}
                        </span>
                      </td>
                      <td className="text-center">
                        <span
                          className="text-primary text-14 fw-bold pointer"
                          title={(item.wbs || []).map((wbs) => wbs.wbs_element).join("\n")}
                          onClick={() => this.openWbsOfProject(item)}
                        >
                          {formatNumber(item.wbs_count ?? 0)}
                        </span>
                      </td>
                      <td className="text-center">{this.renderProjectStatusSelect(item)}</td>
                      <td className="text-center">
                        <div className="d-flex align-items-center gap-2 justify-content-center">
                          <ActionIconButton label={can("projects.update") ? t("edit") : t("view")} icon={can("projects.update") ? ACTION_ICONS.edit : ACTION_ICONS.view} variant={can("projects.update") ? "warning" : "primary"} onClick={() => this.openEditModal(item)} />
                          {can("projects.delete") ? <ActionIconButton label={t("delete")} icon={ACTION_ICONS.delete} variant="danger" onClick={() => this.Delete(item.uid)} /> : null}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer Pagination */}
        <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap text-secondary text-12">
          <div>
            {t("showing")} 1-{data.length} {t("of")} {formatNumber(count)} {t("items")}
          </div>
          <Pagination
            className="mb-0"
            count={Math.ceil(count / PAGE_SIZE) || 1}
            page={page}
            showFirstButton={false}
            showLastButton={false}
            onChange={(_, p) => this.Get(p)}
          />
        </div>
      </>
    );
  }

  renderWbsTab() {
    const { data_wbs, wbs_count, wbs_page, wbs_filter_search, wbs_filter_project_id, data_project } = this.state;

    return (
      <>
        {/* Filter Toolbar */}
        <div className="bg-white rounded-4 shadow-sm p-4 mb-4">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div className="d-flex flex-wrap gap-2 flex-grow-1">
              <div className="position-relative">
                <span className="icon position-absolute top-50 start-0 translate-middle-y ms-3 text-secondary text-12">{""}</span>
                <input
                  type="text"
                  className="form-control ps-5 wpx-350"
                  style={{ height: "38px" }}
                  placeholder={t("search_wbs")}
                  value={wbs_filter_search}
                  onChange={(e) => this.setState({ wbs_filter_search: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && this.GetWbs(1)}
                />
              </div>
              <div className="wpx-300">
                <Select
                  className="form-control"
                  options={data_project}
                  value={wbs_filter_project_id}
                  placeholder={t("all_projects")}
                  defaultLabel={t("all_projects")}
                  onChange={(value) => this.setState({ wbs_filter_project_id: value || "" }, () => this.GetWbs(1))}
                />
              </div>
              <div className="d-flex gap-2">
                <button className="btn btn-secondary px-3 py-2 fw-bold rounded-3 shadow-sm" onClick={() => this.GetWbs(1)}>
                  {t("filter")}
                </button>
                <button
                  className="btn btn-white border px-3 py-2 fw-bold rounded-3 shadow-sm"
                  onClick={() => this.setState({ wbs_filter_search: "", wbs_filter_project_id: "" }, () => this.GetWbs(1))}
                >
                  {t("reset")}
                </button>
              </div>
            </div>
            {can("projects.create") ? (
            <button className="btn btn-primary px-3 py-2 fw-bold rounded-3 shadow-sm d-flex align-items-center" onClick={this.openWbsCreateModal}>
              <span className="me-2 text-16">+</span> {t("add_wbs")}
            </button>
            ) : null}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-4 shadow-sm mb-4 overflow-hidden">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0" style={{ minWidth: "1100px" }}>
              <thead>
                <tr className="text-dark fw-bold text-12 border-bottom border-light">
                  <th className="py-3 border-0">{t("wbs_element")}</th>
                  <th className="py-3 border-0">{t("wbs_element_name")}</th>
                  <th className="py-3 border-0">{t("project_code")}</th>
                  <th className="py-3 border-0">{t("project_name")}</th>
                  <th className="py-3 border-0 text-center">{t("status")}</th>
                  <th className="py-3 border-0 text-center">{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {data_wbs.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center text-secondary py-5">
                      {t("no_data")}
                    </td>
                  </tr>
                ) : (
                  data_wbs.map((item, idx) => (
                    <tr key={idx} className="border-bottom border-light bg-white">
                      <td>
                        <div className="text-secondary text-14">{item.wbs_element}</div>
                      </td>
                      <td>
                        <div className="text-secondary text-14 text-truncate" style={{ maxWidth: "250px" }} title={item.wbs_element_name}>
                          {item.wbs_element_name}
                        </div>
                      </td>
                      <td>
                        <div className="text-secondary text-14">{item.project?.project_code || "-"}</div>
                      </td>
                      <td>
                        <div className="text-secondary text-14 text-truncate" style={{ maxWidth: "250px" }} title={item.project?.name}>
                          {item.project?.name || "-"}
                        </div>
                      </td>
                      <td className="text-center">{this.renderStatusBadge(item.is_active)}</td>
                      <td className="text-center">
                        <div className="d-flex align-items-center gap-2 justify-content-center">
                          <ActionIconButton label={can("projects.update") ? t("edit") : t("view")} icon={can("projects.update") ? ACTION_ICONS.edit : ACTION_ICONS.view} variant={can("projects.update") ? "warning" : "primary"} onClick={() => this.openWbsEditModal(item)} />
                          {can("projects.delete") ? <ActionIconButton label={t("delete")} icon={ACTION_ICONS.delete} variant="danger" onClick={() => this.DeleteWbs(item.uid)} /> : null}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer Pagination */}
        <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap text-secondary text-12">
          <div>
            {t("showing")} 1-{data_wbs.length} {t("of")} {formatNumber(wbs_count)} {t("items")}
          </div>
          <Pagination
            className="mb-0"
            count={Math.ceil(wbs_count / PAGE_SIZE) || 1}
            page={wbs_page}
            showFirstButton={false}
            showLastButton={false}
            onChange={(_, p) => this.GetWbs(p)}
          />
        </div>
      </>
    );
  }

  render() {
    const {
      active_tab,
      modal_create,
      modal_update,
      project_code,
      name,
      project_type_id,
      technician_ids,
      technician_loading,
      tech_dropdown_open,
      tech_search,
      tech_pos,
      owner_code,
      owner_name,
      planned_start_date,
      planned_finish_date,
      actual_start_date,
      actual_finish_date,
      processing_status,
      project_manager,
      cost_center,
      description,
      sap_created_by,
      sap_created_on,
      controlling_area,
      profit_center,
      status,
      modal_hold,
      hold_target,
      modal_wbs_create,
      modal_wbs_update,
      data_project,
      wbs_project_id,
      wbs_element,
      wbs_element_name,
      entity_guid,
      wbs_is_active,
    } = this.state;

    const tabs = [
      { key: TAB_PROJECT, icon: "", label: t("projects") },
      { key: TAB_WBS, icon: "", label: t("wbs_list") },
    ];

    return (
      <div className="w-100 min-vh-100" style={{ backgroundColor: "#F4F7FE" }}>
        <NavbarAdmin />
        <div className="body p-4 mt-3">
          {/* Header Section */}
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
            <div>
              <h3 className="fw-bold mb-1 text-dark">{t("project_management")}</h3>
              <div className="text-secondary text-14">{t("manage_projects_desc")}</div>
            </div>
          </div>

          {/* Tabs */}
          <div className="d-flex border-bottom gap-4 mb-4">
            {tabs.map((tab) => (
              <div
                key={tab.key}
                className={`pb-2 pointer fw-bold d-flex align-items-center ${active_tab === tab.key ? "text-primary" : "text-secondary"}`}
                style={{
                  borderBottom: active_tab === tab.key ? "3px #1976D2 solid" : "",
                }}
                onClick={() => this.switchTab(tab.key)}
              >
                <span className="icon me-2">{tab.icon}</span> {tab.label}
              </div>
            ))}
          </div>

          {active_tab === TAB_PROJECT ? this.renderProjectTab() : this.renderWbsTab()}
        </div>

        {/* MODAL: CREATE / EDIT PROJECT */}
        <Modal
          show={modal_create || modal_update}
          onHide={() => this.setState({ modal_create: false, modal_update: false })}
          centered
          size="lg"
          contentClassName="border-0 rounded-4 shadow-lg"
        >
          <Modal.Header className="border-bottom border-light px-4 py-3 bg-white" closeButton>
            <h5 className="fw-bold mb-0 text-dark">{modal_update ? t("edit_project") : t("add_new_project")}</h5>
          </Modal.Header>
          <Modal.Body className="p-4 bg-white">
<fieldset disabled={this.state.modal_update && !can("projects.update")} className="border-0 p-0 m-0" style={{ minWidth: 0 }}>
            <div className="row g-4">
              <div className="col-12">
                <small className="text-muted d-block mb-3">{t("basic_info_desc")}</small>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label text-14 text-dark mb-2">
                      {t("project_code")} <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control py-2"
                      placeholder={t("enter_project_code")}
                      value={project_code}
                      onChange={(e) => this.setState({ project_code: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-14 text-dark mb-2">
                      {t("project_name")} <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control py-2"
                      placeholder={t("enter_project_name")}
                      value={name}
                      onChange={(e) => this.setState({ name: e.target.value })}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label text-14 text-dark mb-2">
                      {t("project_type")} <span className="text-danger">*</span>
                    </label>
                    <Select
                      className="form-control"
                      options={this.projectTypeOptions()}
                      value={project_type_id}
                      placeholder={t("select_project_type")}
                      onChange={this.onProjectTypeChange}
                    />
                    <div className="text-10 text-secondary mt-1">{t("project_type_from_master_desc")}</div>
                  </div>
                </div>
              </div>

              {/* ข้อมูลโครงการจาก SAP */}
              <div className="col-12 mt-4 border-top pt-4">
                <h6 className="fw-bold mb-1 d-flex align-items-center">
                  <span className="icon text-primary me-2">{"\uf15c"}</span> {t("project_sap_info")}
                </h6>
                <small className="text-muted d-block mb-3">{t("project_sap_info_desc")}</small>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label text-14 text-dark mb-2">{t("owner_code")}</label>
                    {/* valueLabel = รหัสเดิมจาก SAP ที่อาจไม่อยู่ในผลค้นหาปัจจุบัน / ไม่ตรงกับลูกค้าใน master */}
                    <Select
                      className="form-control"
                      options={this.state.data_owner_customers}
                      onSearch={this.SearchOwnerCustomers}
                      nowrapOptions
                      value={owner_code}
                      valueLabel={owner_code}
                      placeholder={t("select_customer")}
                      onChange={this.onOwnerChange}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-14 text-dark mb-2">{t("owner_name")}</label>
                    <input
                      type="text"
                      className="form-control py-2"
                      placeholder={t("owner_name_auto")}
                      value={owner_name}
                      disabled
                      readOnly
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-14 text-dark mb-2">
                      {t("planned_start_date")} <span className="text-danger">*</span>
                    </label>
                    <DatePicker
                      type="th"
                      className="form-control"
                      value={planned_start_date}
                      onChange={(val) => this.setState({ planned_start_date: val || "" })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-14 text-dark mb-2">
                      {t("planned_finish_date")} <span className="text-danger">*</span>
                    </label>
                    <DatePicker
                      type="th"
                      className="form-control"
                      value={planned_finish_date}
                      onChange={(val) => this.setState({ planned_finish_date: val || "" })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-14 text-dark mb-2">{t("actual_start_date")}</label>
                    <DatePicker
                      type="th"
                      className="form-control"
                      value={actual_start_date}
                      onChange={(val) => this.setState({ actual_start_date: val || "" })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-14 text-dark mb-2">{t("actual_finish_date")}</label>
                    <DatePicker
                      type="th"
                      className="form-control"
                      value={actual_finish_date}
                      onChange={(val) => this.setState({ actual_finish_date: val || "" })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-14 text-dark mb-2">
                      {t("processing_status")} <span className="text-danger">*</span>
                    </label>
                    <Select
                      className="form-control"
                      options={processingStatusOptions()}
                      value={processing_status}
                      placeholder={t("select_processing_status")}
                      onChange={(value) =>
                        this.setState({
                          processing_status: value || DEFAULT_PROCESSING_STATUS,
                        })
                      }
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-14 text-dark mb-2">
                      {t("project_manager")}
                    </label>
                    <input
                      type="text"
                      className="form-control py-2"
                      placeholder={t("enter_project_manager")}
                      value={project_manager}
                      onChange={(e) => this.setState({ project_manager: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-14 text-dark mb-2">
                      {t("cost_center")} <span className="text-danger">*</span>
                    </label>
                    <Select
                      className="form-control"
                      options={this.costCenterOptions()}
                      value={cost_center}
                      placeholder={t("select_department")}
                      onChange={(value) => this.setState({ cost_center: value || "" })}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label text-14 text-dark mb-2">{t("description")}</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      placeholder={t("enter_project_description")}
                      value={description}
                      onChange={(e) => this.setState({ description: e.target.value })}
                    />
                  </div>

                  {/* ช่างผู้รับผิดชอบ - เลือกได้หลายคน เฉพาะช่างที่มี department access ตรงกับแผนกของประเภทงานที่เลือก */}
                  <div className="col-12">
                    <label className="form-label text-14 text-dark mb-2">
                      {t("responsible_technicians")}
                      <span className="text-secondary text-12 ms-1">{t("multi_select_technicians_hint")}</span>
                    </label>
                    <div className="position-relative">
                      <div
                        ref={this.techTriggerRef}
                        className={`form-control d-flex align-items-center ${tech_dropdown_open ? "border-primary" : ""} ${!project_type_id ? "bg-light" : ""}`}
                        style={{
                          minHeight: "38px",
                          height: "auto",
                          cursor: project_type_id ? "pointer" : "not-allowed",
                        }}
                        onClick={this.toggleTechDropdown}
                      >
                        {/* ช่องแสดงแค่จำนวนที่เลือก - รายชื่อดู/แก้ใน dropdown (checkbox) เท่านั้น */}
                        <div className="d-flex align-items-center flex-grow-1 py-1">
                          {technician_ids.length === 0 ? (
                            <span className="text-secondary text-14">
                              {project_type_id ? t("select_responsible_technicians") : t("select_project_type_first")}
                            </span>
                          ) : (
                            <span className="text-primary text-14 fw-bold">{t("selected_technicians_count").replace("{count}", formatNumber(technician_ids.length))}</span>
                          )}
                        </div>
                        <span className="icon text-secondary ms-2">{tech_dropdown_open ? "\uf077" : "\uf078"}</span>
                      </div>

                      {tech_dropdown_open && (
                        <>
                          <div className="position-fixed" style={{ inset: 0, zIndex: 1040 }} onClick={this.toggleTechDropdown} />
                          <div
                            className="bg-white border rounded-3 shadow p-2"
                            style={{
                              position: "fixed",
                              zIndex: 1050,
                              overflowY: "auto",
                              left: tech_pos?.left,
                              width: tech_pos?.width,
                              top: tech_pos?.top,
                              bottom: tech_pos?.bottom,
                              maxHeight: tech_pos?.maxHeight || 320,
                            }}
                          >
                            <input
                              type="text"
                              className="form-control mb-2"
                              placeholder={t("search_technicians")}
                              value={tech_search}
                              autoFocus
                              onChange={(e) => this.setState({ tech_search: e.target.value })}
                            />
                            {this.filterTechnicians().map((technician) => {
                              const checked = technician_ids.includes(technician.uid);
                              return (
                                <label
                                  key={technician.uid}
                                  className="d-flex align-items-center gap-2 px-2 py-2 rounded-2 hover-bg-light"
                                  style={{ cursor: "pointer" }}
                                >
                                  <input
                                    type="checkbox"
                                    className="form-check-input mt-0"
                                    checked={checked}
                                    onChange={() => this.toggleTechnician(technician.uid)}
                                  />
                                  <span className="badge bg-primary-light text-primary border border-primary px-2 py-1 text-12">
                                    {technician.technician_code || "-"}
                                  </span>
                                  <span className="text-14 text-dark">{technicianName(technician).replace(`${technician.technician_code} - `, "")}</span>
                                </label>
                              );
                            })}
                            {this.filterTechnicians().length === 0 && (
                              <div className="text-center text-secondary text-12 py-3">
                                {technician_loading ? t("loading") : t("technician_not_found_for_project_type")}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    <div className="text-10 text-secondary mt-1">{t("responsible_technicians_desc")}</div>
                  </div>
                  {(controlling_area || profit_center) && (
                    <div className="col-12 text-secondary text-12">
                      {t("controlling_area")}: {controlling_area || "-"} · {t("profit_center")}: {profit_center || "-"}
                    </div>
                  )}
                  {(sap_created_by || sap_created_on) && (
                    <div className="col-12 text-secondary text-12">
                      {t("sap_created_by")}: {sap_created_by || "-"} · {t("sap_created_on")}:{" "}
                      {sap_created_on ? `${new Date(sap_created_on).toISOString().slice(0, 19).replace("T", " ")} UTC` : "-"}
                    </div>
                  )}
                </div>
              </div>

              {this.renderStatusRadio(
                "projectStatus",
                status,
                (value) => this.setState({ status: value }),
                projectStatusOptions(),
                t("project_status_desc"),
                status === "hold" ? this.renderHoldEffects("mt-3") : null
              )}
            </div>
          </fieldset>
</Modal.Body>
          <Modal.Footer className="border-top border-light px-4 py-3 bg-white d-flex justify-content-end gap-2">
            <button
              className="btn btn-white border px-4 py-2 text-dark rounded-3 fw-bold"
              onClick={() => this.setState({ modal_create: false, modal_update: false })}
            >
              {t("cancel")}
            </button>
            {(this.state.uid ? can("projects.update") : can("projects.create")) ? (
            <button className="btn btn-primary px-4 py-2 d-flex align-items-center rounded-3 fw-bold text-white shadow-sm" onClick={this.CreateUpdate}>
              <span className="icon me-2">{""}</span> {t("save")}
            </button>
            ) : null}
          </Modal.Footer>
        </Modal>

        {/* MODAL: ยืนยันระงับโครงการชั่วคราว (เลือก hold จาก select ในตาราง) */}
        <Modal show={modal_hold} onHide={this.closeHoldModal} centered contentClassName="border-0 rounded-4 shadow-lg">
          <Modal.Body className="p-4 bg-white text-center">
            <div
              className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle bg-warning-light text-warning"
              style={{ width: "72px", height: "72px" }}
            >
              <span className="icon text-24">{"\uf04c"}</span>
            </div>
            {/* หัวข้อใส่ชื่อโครงการลงไปแทน {name} - t() ไม่มี interpolation จึง replace เอง */}
            <h5 className="fw-bold text-dark mb-3">{t("hold_project_title").replace("{name}", hold_target?.name || hold_target?.project_code || "-")}</h5>
            {this.renderHoldEffects("mb-4")}
            <div className="d-flex justify-content-center gap-2">
              <button className="btn btn-white border px-4 py-2 text-dark rounded-3 fw-bold" onClick={this.closeHoldModal}>
                {t("cancel")}
              </button>
              <button className="btn btn-warning px-4 py-2 rounded-3 fw-bold text-white shadow-sm" onClick={this.confirmHold}>
                {t("confirm_hold")}
              </button>
            </div>
          </Modal.Body>
        </Modal>

        {/* MODAL: CREATE / EDIT WBS */}
        <Modal
          show={modal_wbs_create || modal_wbs_update}
          onHide={() => this.setState({ modal_wbs_create: false, modal_wbs_update: false })}
          centered
          size="lg"
          contentClassName="border-0 rounded-4 shadow-lg"
        >
          <Modal.Header className="border-bottom border-light px-4 py-3 bg-white" closeButton>
            <h5 className="fw-bold mb-0 text-dark">{modal_wbs_update ? t("edit_wbs") : t("add_new_wbs")}</h5>
          </Modal.Header>
          <Modal.Body className="p-4 bg-white">
<fieldset disabled={this.state.modal_wbs_update && !can("projects.update")} className="border-0 p-0 m-0" style={{ minWidth: 0 }}>
            <div className="row g-4">
              <div className="col-12">
                <small className="text-muted d-block mb-3">{t("basic_info_desc")}</small>
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label text-14 text-dark mb-2">
                      {t("label_project")} <span className="text-danger">*</span>
                    </label>
                    <Select
                      className="form-control"
                      options={data_project}
                      value={wbs_project_id}
                      placeholder={t("select_project")}
                      onChange={(value) => this.setState({ wbs_project_id: value || "" })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-14 text-dark mb-2">
                      {t("wbs_element")} <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control py-2"
                      placeholder={t("enter_wbs_element")}
                      value={wbs_element}
                      onChange={(e) => this.setState({ wbs_element: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-14 text-dark mb-2">{t("wbs_element_name")}</label>
                    <input
                      type="text"
                      className="form-control py-2"
                      placeholder={t("enter_wbs_element_name")}
                      value={wbs_element_name}
                      onChange={(e) => this.setState({ wbs_element_name: e.target.value })}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label text-14 text-dark mb-2">{t("entity_guid")}</label>
                    <input
                      type="text"
                      className="form-control py-2"
                      placeholder={t("enter_entity_guid")}
                      value={entity_guid}
                      onChange={(e) => this.setState({ entity_guid: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {this.renderStatusRadio("wbsStatus", wbs_is_active, (value) => this.setState({ wbs_is_active: value }), [
                { value: "Active", label: t("active") },
                { value: "Inactive", label: t("inactive") },
              ])}
            </div>
          </fieldset>
</Modal.Body>
          <Modal.Footer className="border-top border-light px-4 py-3 bg-white d-flex justify-content-end gap-2">
            <button
              className="btn btn-white border px-4 py-2 text-dark rounded-3 fw-bold"
              onClick={() =>
                this.setState({
                  modal_wbs_create: false,
                  modal_wbs_update: false,
                })
              }
            >
              {t("cancel")}
            </button>
            {(this.state.wbs_uid ? can("projects.update") : can("projects.create")) ? (
            <button className="btn btn-primary px-4 py-2 d-flex align-items-center rounded-3 fw-bold text-white shadow-sm" onClick={this.CreateUpdateWbs}>
              <span className="icon me-2">{""}</span> {t("save")}
            </button>
            ) : null}
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}
